import { useState, useEffect } from "react";

/**
 * Long/Short Position Plan tool — TradingView-style risk/reward planner.
 * Renders draggable Entry/SL/TP price lines on the chart (via Chart.setDraggable + onDragLine).
 * Shows live R:R + $ P&L. Click EXECUTE to convert plan into a real position; DISCARD to cancel.
 */
export default function PositionPlanCard({ plan, lastPrice, balance, riskPct, onUpdate, onExecute, onDiscard }) {
  if (!plan) return null;
  const { side, entryPrice, stopLoss, takeProfit } = plan;
  const risk = Math.abs(entryPrice - stopLoss);
  const reward = Math.abs(takeProfit - entryPrice);
  const rr = risk > 0 ? reward / risk : 0;
  const dollarRisk = balance * (riskPct / 100);
  const size = risk > 0 ? dollarRisk / (risk * 2) : 0;
  const projLoss = -dollarRisk;
  const projProfit = reward * size * 2;
  const currentPnL = ((lastPrice - entryPrice) * (side === "LONG" ? 1 : -1)) * size * 2;
  const isLong = side === "LONG";

  return (
    <div data-testid="position-plan-card" className={`bg-slate-900/80 border rounded-xl p-3 space-y-2 text-[11px] font-mono ${isLong ? "border-emerald-500/40" : "border-rose-500/40"}`}>
      <div className="flex justify-between items-center">
        <div className={`px-2 py-0.5 rounded text-[10px] font-bold ${isLong ? "bg-emerald-600/20 text-emerald-300" : "bg-rose-600/20 text-rose-300"}`}>
          {side} PLAN
        </div>
        <div className={`text-[10px] font-bold ${rr >= 2 ? "text-emerald-400" : rr >= 1 ? "text-amber-400" : "text-rose-400"}`}>
          R:R {rr.toFixed(2)}
        </div>
      </div>
      <div className="grid grid-cols-3 gap-1.5">
        <Cell label="ENTRY" value={entryPrice.toFixed(2)} color="text-slate-300"/>
        <Cell label="SL" value={stopLoss.toFixed(2)} color="text-rose-400"/>
        <Cell label="TP" value={takeProfit.toFixed(2)} color="text-emerald-400"/>
      </div>
      <div className="grid grid-cols-3 gap-1.5">
        <Cell label="SIZE" value={size.toFixed(2)} color="text-blue-400"/>
        <Cell label="MAX LOSS" value={`-$${Math.abs(projLoss).toFixed(0)}`} color="text-rose-400"/>
        <Cell label="MAX WIN" value={`+$${projProfit.toFixed(0)}`} color="text-emerald-400"/>
      </div>
      <div className={`text-center text-[11px] font-bold py-1 rounded ${currentPnL >= 0 ? "bg-emerald-500/10 text-emerald-300" : "bg-rose-500/10 text-rose-300"}`}>
        Live: {currentPnL >= 0 ? "+" : ""}${currentPnL.toFixed(2)}
      </div>
      <div className="text-[9px] text-slate-500"><i className="fa-solid fa-hand-pointer mr-1"/> Drag SL/TP lines on the chart to adjust</div>
      <div className="grid grid-cols-2 gap-1.5">
        <button data-testid="plan-execute" onClick={onExecute}
          className={`py-2 rounded-lg text-xs font-bold ${isLong ? "bg-emerald-600 hover:bg-emerald-700" : "bg-rose-600 hover:bg-rose-700"} text-white`}>
          EXECUTE
        </button>
        <button data-testid="plan-discard" onClick={onDiscard}
          className="py-2 rounded-lg bg-slate-800 hover:bg-slate-700 border border-slate-700 text-xs font-bold text-slate-300">
          DISCARD
        </button>
      </div>
    </div>
  );
}

function Cell({ label, value, color }) {
  return (
    <div className="bg-slate-900/60 border border-slate-800/60 rounded px-1.5 py-1">
      <div className="text-[9px] text-slate-500">{label}</div>
      <div className={`font-bold ${color}`}>{value}</div>
    </div>
  );
}
